import { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import bonusesService from "../shared/services/bonusesService";
import vipService from "../shared/services/vipService";
import logger from "../shared/utils/logger";

const ClaimIndicatorsContext = createContext();

export const useClaimIndicators = () => {
  const context = useContext(ClaimIndicatorsContext);
  if (!context) {
    throw new Error(
      "useClaimIndicators must be used within a ClaimIndicatorsProvider",
    );
  }
  return context;
};

export const ClaimIndicatorsProvider = ({ children }) => {
  const { user, isLoggedIn, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [showCalendarGlow, setShowCalendarGlow] = useState(false);
  const [showVipGlow, setShowVipGlow] = useState(false);
  const [lastFetch, setLastFetch] = useState(0);

  // Cache for 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchClaimStatus = async (force = false) => {
    const now = Date.now();

    // Skip if not logged in or recently fetched (unless forced)
    if (
      !isLoggedIn ||
      !user?.id ||
      (!force && now - lastFetch < CACHE_DURATION)
    ) {
      if (!isLoggedIn || !user?.id) {
        setShowCalendarGlow(false);
        setShowVipGlow(false);
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      logger.info("ClaimIndicators: Fetching claim status", {
        userId: user.id,
      });

      // Fetch calendar state
      const calendar = await bonusesService.getCalendarState(user.id);
      const canClaimCalendarToday = !!calendar?.canClaimToday;

      // Fetch VIP daily status with timeout
      let canClaimVipToday = false;
      try {
        const vipPromise = vipService.getDailyStatus();
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve(null), 3000),
        );

        const vip = await Promise.race([vipPromise, timeoutPromise]);
        if (vip) {
          const statuses = vip?.statuses || {};
          const rewards = vip?.rewards || {};

          // Only consider claimable if canClaim is true AND reward amount > 0
          canClaimVipToday = Boolean(
            (statuses.gld?.canClaim && rewards.gld > 0) ||
            (statuses.cb?.canClaim && rewards.cb > 0) ||
            (statuses.feathers?.canClaim && rewards.feathers > 0),
          );

          logger.info("ClaimIndicators: VIP status", {
            statuses,
            rewards,
            canClaimVipToday,
            breakdown: {
              gld: `canClaim: ${statuses.gld?.canClaim}, amount: ${rewards.gld}`,
              cb: `canClaim: ${statuses.cb?.canClaim}, amount: ${rewards.cb}`,
              feathers: `canClaim: ${statuses.feathers?.canClaim}, amount: ${rewards.feathers}`,
            },
          });
        } else {
          logger.warn("ClaimIndicators: VIP status timed out");
        }
      } catch (vipError) {
        logger.warn("ClaimIndicators: VIP status failed:", vipError);
      }

      setShowCalendarGlow(canClaimCalendarToday);
      setShowVipGlow(canClaimVipToday);
      setLastFetch(now);

      logger.info("ClaimIndicators: Status updated", {
        canClaimCalendarToday,
        canClaimVipToday,
        showBuyGlow: canClaimCalendarToday || canClaimVipToday,
      });
    } catch (error) {
      logger.warn("ClaimIndicators: Failed to fetch status", error);
      setShowCalendarGlow(false);
      setShowVipGlow(false);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch when user logs in
  useEffect(() => {
    if (!authLoading) {
      fetchClaimStatus();
    }
  }, [user?.id, isLoggedIn, authLoading]);

  // Listen for claim events to refresh status
  useEffect(() => {
    const handleClaimEvent = () => {
      logger.info("ClaimIndicators: Claim event detected, refreshing...");
      fetchClaimStatus(true); // Force refresh
    };

    window.addEventListener("vip-daily-claimed", handleClaimEvent);
    window.addEventListener("calendar-claimed", handleClaimEvent);

    return () => {
      window.removeEventListener("vip-daily-claimed", handleClaimEvent);
      window.removeEventListener("calendar-claimed", handleClaimEvent);
    };
  }, [user?.id]);

  const value = {
    loading,
    showCalendarGlow,
    showVipGlow,
    showBuyGlow: showCalendarGlow || showVipGlow,
    refreshClaimStatus: () => fetchClaimStatus(true),
  };

  return (
    <ClaimIndicatorsContext.Provider value={value}>
      {children}
    </ClaimIndicatorsContext.Provider>
  );
};
